import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import PercentageFieldWithLabel from '../Beneficiary/PercentageFieldWithLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapPin } from '@fortawesome/pro-light-svg-icons';
import { BeneficiaryValuesBox } from '../Beneficiary/BeneficiaryValues/BeneficiaryMultiValues';
import { AddressValue } from './Locations';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { DisabledSuggestions, getTotalPercentage } from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

const StyledDiv = styled.div`
  .location-instructions {
    text-align: center;
    .percentage-helper-text {
      margin-top: ${SPACERS.medium};
      div.gap {
        height: ${SPACERS.large};
      }
    }
  }
  .error-text {
    margin-top: ${SPACERS.small};
  }
`;

const ValidationMessage: React.FC<{
  isAnyDisabled?: boolean;
  percentage: number;
  totalAddresses: number;
}> = ({ isAnyDisabled, percentage, totalAddresses }) => {
  if (isAnyDisabled) {
    return (
      <TrueImpactText
        variant="body-small"
        color="critical"
        className="error-text"
      >
        *Please remove the overlapping locations.
      </TrueImpactText>
    );
  }
  if (totalAddresses === 0) return null;
  switch (percentage) {
    case 100:
      return null;

    default:
      return (
        <TrueImpactText
          variant="body-small"
          color="critical"
          className="error-text"
        >
          Total percentage(s) must equal 100%.
        </TrueImpactText>
      );
  }
};

const getPercentageFields = ({
  selectedAddresses,
  disabledSuggestions,
  handleRemove,
  handlePercentage,
}: LocationPercentageProps) => {
  let isAnyDisabled = false;
  const fields = selectedAddresses.map((a, i) => {
    const disabled = disabledSuggestions[a.address || ''];
    if (disabled) {
      isAnyDisabled = true;
    }
    return (
      <PercentageFieldWithLabel
        key={a.address || i}
        label={a.address || 'Unknown'}
        focusOnMount={true}
        initialValue={a.percentage || a.percent || null}
        onBlur={v => {
          handlePercentage(v, i);
        }}
        onRemove={() => handleRemove(i)}
        disabled={disabled}
        className="content-row"
      />
    );
  });
  return { isAnyDisabled, fields };
};

interface LocationPercentageProps {
  selectedAddresses: AddressValue[];
  disabledSuggestions: DisabledSuggestions;
  handlePercentage: (percentage: number | null, index: number) => unknown;
  handleRemove: (index: number) => unknown;
}

const LocationPercentage: React.FC<LocationPercentageProps> = ({
  selectedAddresses,
  disabledSuggestions,
  handlePercentage,
  handleRemove,
}) => {
  const { isAnyDisabled, fields } = getPercentageFields({
    selectedAddresses,
    disabledSuggestions,
    handlePercentage,
    handleRemove,
  });
  const percentage = getTotalPercentage(selectedAddresses);
  return (
    <StyledDiv>
      <BeneficiaryValuesBox className="header-grey">
        <div className="header">Locations</div>
        <div className="content-wrapper">
          {selectedAddresses.length === 0 ? (
            <div className="no-result">
              <FontAwesomeIcon
                size="2x"
                icon={faMapPin}
                color={COLORS.greyDark}
              />
              <TrueImpactText
                variant="body"
                color="greyDark"
                className="no-result-text"
              >
                Your selected locations will appear here.
              </TrueImpactText>
            </div>
          ) : (
            fields
          )}
          <ValidationMessage
            isAnyDisabled={isAnyDisabled}
            percentage={percentage}
            totalAddresses={selectedAddresses.length}
          />
        </div>
      </BeneficiaryValuesBox>
      {selectedAddresses.length !== 0 && (
        <div className="location-instructions">
          <TrueImpactText variant="body" className="percentage-helper-text">
            Please let us know the percentages of services provided in each
            location (based on number of people served). Approximations are
            totally fine!
            <div className="gap" />
            <strong>An example:</strong> If one of your locations serves 25
            youth out of a total of 100 youth served by the program, then that
            location would account for 25 percent.
          </TrueImpactText>
        </div>
      )}
    </StyledDiv>
  );
};

export default LocationPercentage;
